/**
 * Reading the backend's per-field errors back out of an API error.
 *
 * Two things stand between a refusal and the field it is about.
 *
 * The body is run through `camelizeKeys` (`@ixicare/resources`'s `fetchErrors.ts`), and humps
 * camelises *deeply* — so `field_errors` keys are rewritten too, and a field whose name is not
 * already camelCase cannot be found under the name the backend used. Every caller today happens
 * to ask for a camelCase name and so never noticed. A schema-driven form cannot: its field ids
 * are the configuration keys themselves (`indoor-geofencing-zones`, `periodic_gps_period`), and
 * those arrive rewritten. Hence the lookup tries the id as given and then its camelised form,
 * which leaves every existing caller working unchanged.
 *
 * And a message is not always a list of strings. marshmallow reports a `List(String())` as a dict
 * keyed by index, and a nested schema as a dict of dicts, so `.join(', ')` on whatever arrived
 * threw a TypeError for exactly the fields this form needs.
 */

import { WatcherrApiError } from '@ixicare/resources';

/**
 * Mirrors humps for the keys that reach it: lowercase kebab-case and snake_case, which is what
 * both the configuration catalog and marshmallow's `data_key`s are. Local rather than imported
 * because humps is only a transitive dependency here.
 */
const camelise = (key: string): string => key.replace(/[-_](\w)/g, (_match, char: string) => char.toUpperCase());

/**
 * Every message in whatever shape it arrived, in order.
 *
 * The index keys of a list error are dropped rather than shown: one control holds the whole list,
 * so "item 2 is invalid" has nowhere to point, and the messages themselves say enough.
 */
const flatten = (messages: unknown): string[] => {
  if (messages === null || messages === undefined) return [];
  if (typeof messages === 'string') return messages.length ? [messages] : [];
  if (Array.isArray(messages)) return messages.flatMap(flatten);
  if (typeof messages === 'object') return Object.values(messages).flatMap(flatten);
  return [String(messages)];
};

/** The backend's refusals for one field, as one sentence, or nothing if it did not name it. */
export const getFieldError = (key: string, error?: Error): string | undefined => {
  if (!(error instanceof WatcherrApiError)) return undefined;

  const fieldErrors = error.body?.fieldErrors as Record<string, unknown> | undefined;
  if (!fieldErrors) return undefined;

  const messages = flatten(fieldErrors[key] ?? fieldErrors[camelise(key)]);
  return messages.length ? messages.join(', ') : undefined;
};

/** Whether the backend named any field at all, which is what decides between showing the errors
 * beside the fields and showing the general message on its own. */
export const hasFieldErrors = (error?: Error): boolean =>
  error instanceof WatcherrApiError && Object.keys(error.body?.fieldErrors ?? {}).length > 0;
